import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { resolveUrl } from "@/lib/queryClient";
import { Capacitor } from "@capacitor/core";
import TournamentHistoryCard from "./tournament-history-card";
import { buildInscriptionMessage, mailHref, smsHref } from "@/lib/inscriptionMessage";

// ── useState persisté dans localStorage (setter = valeur directe) ────────────────
function useLocalState<T>(key: string, defaultValue: T): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored !== null ? (JSON.parse(stored) as T) : defaultValue;
    } catch {
      return defaultValue;
    }
  });
  const set = useCallback(
    (next: T) => {
      setValue(next);
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* ignore */
      }
    },
    [key]
  );
  return [value, set];
}

const CATEGORIES = ["P25", "P50", "P100", "P250", "P500", "P1000", "P1500", "P2000"];
const AGE_CATEGORIES = ["Senior", "45 ans", "55 ans", "65 ans"];
const EPREUVES = [
  { value: "DM", label: "Messieurs" },
  { value: "DD", label: "Dames" },
  { value: "DX", label: "Mixte" },
];
const EPREUVE_LABELS: Record<string, string> = { DM: "Messieurs", DD: "Dames", DX: "Mixte" };

export const CAT_COLORS: Record<string, string> = {
  P25: "#64748b", P50: "#14b8a6", P100: "#22c55e", P250: "#3b82f6",
  P500: "#a855f7", P1000: "#f59e0b", P1500: "#ef4444", P2000: "#ec4899",
};

interface Joueur {
  idCrm: string | null;
  prenom: string;
  nom: string;
  club?: string;
  rank: number | null;
  licence?: string | null; // undefined = joueur mémorisé avant l'ajout de la licence → à récupérer
}

interface Geo {
  lat: number;
  lng: number;
  label: string;
}

// ── Multi-select dropdown (restyle Tailwind, logique identique à ClubDropdown) ───
function Checkbox({ checked, indeterminate = false }: { checked: boolean; indeterminate?: boolean }) {
  return (
    <span
      className={`inline-flex items-center justify-center w-4 h-4 rounded border shrink-0 ${
        checked || indeterminate
          ? "bg-[#001489] border-[#001489]"
          : "bg-transparent border-gray-300 dark:border-border"
      }`}
      aria-hidden="true"
    >
      <svg viewBox="0 0 10 10" fill="none" className="w-3 h-3">
        {indeterminate ? (
          <line x1="2" y1="5" x2="8" y2="5" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
        ) : checked ? (
          <polyline points="1.5,5 4,7.5 8.5,2.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        ) : null}
      </svg>
    </span>
  );
}

interface DropdownOption {
  name: string;
}

function MultiDropdown({
  clubs,
  selected,
  onChange,
  allLabel,
  noneLabel = "Aucun club",
  selectedLabel,
  renderLabel = (c: DropdownOption) => c.name,
}: {
  clubs: DropdownOption[];
  selected: string[] | null;
  onChange: (next: string[] | null) => void;
  allLabel?: string;
  noneLabel?: string;
  selectedLabel?: (n: number) => string;
  renderLabel?: (c: DropdownOption) => string;
}) {
  const [open, setOpen] = useState(false);

  const allChecked = selected === null || clubs.every((c) => selected.includes(c.name));
  const someChecked = !allChecked && selected !== null && selected.length > 0;

  function toggleAll() {
    onChange(allChecked ? [] : null);
  }
  function toggleClub(name: string) {
    const current = selected === null ? clubs.map((c) => c.name) : selected;
    const next = current.includes(name) ? current.filter((n) => n !== name) : [...current, name];
    onChange(next.length === clubs.length ? null : next);
  }

  const label = allChecked
    ? allLabel ?? `Tous les clubs (${clubs.length})`
    : selected === null || selected.length === 0
    ? noneLabel
    : (selectedLabel ?? ((n: number) => `${n} club${n > 1 ? "s" : ""} sélectionné${n > 1 ? "s" : ""}`))(selected.length);

  return (
    <div className="relative">
      {open && <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />}
      <button
        type="button"
        className="w-full flex items-center justify-between gap-2 px-3 h-9 text-xs rounded-md bg-gray-50 dark:bg-muted border border-transparent hover:border-gray-300 dark:hover:border-border text-foreground"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="truncate">{label}</span>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="shrink-0 text-muted-foreground">
          <path
            d={open ? "M2 8l4-4 4 4" : "M2 4l4 4 4-4"}
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-72 overflow-auto rounded-md bg-white dark:bg-card border border-gray-200 dark:border-border shadow-lg py-1">
          <div
            className="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-gray-50 dark:hover:bg-muted"
            onClick={toggleAll}
          >
            <Checkbox checked={allChecked} indeterminate={someChecked} />
            <span>{allChecked ? "Tout désélectionner" : "Tout sélectionner"}</span>
          </div>
          <div className="h-px bg-gray-100 dark:bg-border my-1" />
          {clubs.map((c) => {
            const checked = allChecked || (selected !== null && selected.includes(c.name));
            return (
              <div
                key={c.name}
                className="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-gray-50 dark:hover:bg-muted"
                onClick={() => toggleClub(c.name)}
              >
                <Checkbox checked={checked} />
                <span className="truncate">{renderLabel(c)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Barre localisation (texte + géoloc) + rayon ─────────────────────────────────
function LocationBar({
  geo,
  onGeoChange,
  rayon,
  onRayonChange,
}: {
  geo: Geo | null;
  onGeoChange: (g: Geo | null) => void;
  rayon: number;
  onRayonChange: (r: number) => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [locating, setLocating] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    let alive = true;
    const t = setTimeout(() => {
      fetch(resolveUrl(`/api/tournois/geocode?q=${encodeURIComponent(q)}`))
        .then((r) => r.json())
        .then((d) => {
          if (alive) setResults(Array.isArray(d) ? d : []);
        })
        .catch(() => {
          if (alive) setResults([]);
        });
    }, 300);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [query]);

  function selectPlace(p: any) {
    onGeoChange({ lat: p.lat, lng: p.lng, label: p.label });
    setQuery("");
    setResults([]);
    setOpen(false);
  }

  async function useMyLocation() {
    const GeoPlugin = (Capacitor as any).Plugins?.Geolocation;
    // App native (Android/iOS) : plugin Capacitor + permissions runtime
    if (Capacitor.isNativePlatform?.() && GeoPlugin) {
      setLocating(true);
      try {
        const perm = await GeoPlugin.requestPermissions({ permissions: ["location"] });
        const state = perm?.location ?? perm?.coarseLocation;
        if (state && state !== "granted") {
          setLocating(false);
          alert("Autorisation de localisation refusée");
          return;
        }
        const pos = await GeoPlugin.getCurrentPosition({ enableHighAccuracy: false, timeout: 10000 });
        onGeoChange({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: "Ma position" });
      } catch {
        alert("Position refusée ou indisponible");
      } finally {
        setLocating(false);
      }
      return;
    }
    // Web : API navigateur
    if (!navigator.geolocation) {
      alert("Géolocalisation non disponible");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onGeoChange({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: "Ma position" });
        setLocating(false);
      },
      () => {
        setLocating(false);
        alert("Position refusée ou indisponible");
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
      <div className="flex-1" ref={boxRef}>
        <span className="block text-[11px] font-semibold text-muted-foreground mb-1">Localisation</span>
        {geo ? (
          <div className="flex items-center justify-between gap-2 px-3 h-9 rounded-md bg-gray-50 dark:bg-muted border border-transparent">
            <span className="text-xs truncate">📍 {geo.label}</span>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground text-base leading-none"
              onClick={() => onGeoChange(null)}
              aria-label="Retirer"
            >
              ×
            </button>
          </div>
        ) : (
          <div className="relative flex gap-1">
            <input
              type="text"
              className="flex-1 px-3 h-9 text-xs rounded-md bg-gray-50 dark:bg-muted border border-transparent focus:border-[#001489] outline-none text-foreground"
              placeholder="Ville, adresse…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
            />
            <button
              type="button"
              className="shrink-0 w-9 h-9 rounded-md bg-gray-50 dark:bg-muted border border-transparent hover:border-gray-300 dark:hover:border-border"
              onClick={useMyLocation}
              disabled={locating}
              title="Ma position"
            >
              {locating ? "…" : "📍"}
            </button>
            {open && query.trim().length >= 2 && (
              <ul className="absolute top-full left-0 mt-1 w-full z-20 rounded-md bg-white dark:bg-card border border-gray-200 dark:border-border shadow-lg py-1 max-h-64 overflow-auto">
                {results.length === 0 && <li className="px-3 py-2 text-xs text-muted-foreground">Aucun lieu</li>}
                {results.map((p, i) => (
                  <li key={i}>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-muted"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => selectPlace(p)}
                    >
                      {p.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      <label className="sm:w-56 block">
        <span className="block text-[11px] font-semibold text-muted-foreground mb-1">Rayon : {rayon} km</span>
        <input
          type="range"
          min={1}
          max={200}
          step={1}
          value={rayon}
          onChange={(e) => onRayonChange(parseInt(e.target.value, 10))}
          disabled={!geo}
          className="w-full accent-[#001489] disabled:opacity-40"
        />
      </label>
    </div>
  );
}

// ── Picker joueur FFT (recherche débouncée) ─────────────────────────────────────
function PlayerPicker({
  label,
  value,
  onChange,
  suggestions,
  suggestionsLabel = "Favoris",
}: {
  label: string;
  value: Joueur | null;
  onChange: (j: Joueur | null) => void;
  suggestions?: Joueur[];
  suggestionsLabel?: string;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Joueur[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => {
      fetch(resolveUrl(`/api/players/padel?q=${encodeURIComponent(q)}&page=1`))
        .then((r) => r.json())
        .then((data) => {
          if (alive) setResults(Array.isArray(data?.players) ? data.players.slice(0, 8) : []);
        })
        .catch(() => {
          if (alive) setResults([]);
        })
        .finally(() => {
          if (alive) setLoading(false);
        });
    }, 300);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [query]);

  function select(p: Joueur) {
    onChange(p);
    setQuery("");
    setResults([]);
    setOpen(false);
  }

  return (
    <div className="relative" ref={boxRef}>
      <span className="block text-[11px] font-semibold text-muted-foreground mb-1">{label}</span>
      {value ? (
        <div className="flex items-center justify-between gap-2 px-3 h-9 rounded-md bg-gray-50 dark:bg-muted border border-transparent">
          <span className="text-xs truncate">
            {value.prenom} {value.nom}
            {value.rank != null && <em className="not-italic text-muted-foreground"> #{value.rank}</em>}
          </span>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground text-base leading-none"
            onClick={() => onChange(null)}
            aria-label="Retirer"
          >
            ×
          </button>
        </div>
      ) : (
        <div className="relative">
          <input
            type="text"
            className="w-full px-3 h-9 text-xs rounded-md bg-gray-50 dark:bg-muted border border-transparent focus:border-[#001489] outline-none text-foreground"
            placeholder="Rechercher un joueur…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
          />
          {open && query.trim().length >= 2 && (
            <ul className="absolute top-full left-0 mt-1 w-full z-20 rounded-md bg-white dark:bg-card border border-gray-200 dark:border-border shadow-lg py-1 max-h-64 overflow-auto">
              {loading && <li className="px-3 py-2 text-xs text-muted-foreground">Recherche…</li>}
              {!loading && results.length === 0 && <li className="px-3 py-2 text-xs text-muted-foreground">Aucun joueur</li>}
              {results.map((p, i) => (
                <li key={i}>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-muted"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => select(p)}
                  >
                    {p.prenom} {p.nom}
                    {p.rank != null && <em className="not-italic text-muted-foreground"> #{p.rank}</em>}
                    {p.club && <span className="text-muted-foreground"> · {p.club}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {open && query.trim().length < 2 && suggestions && suggestions.length > 0 && (
            <ul className="absolute top-full left-0 mt-1 w-full z-20 rounded-md bg-white dark:bg-card border border-gray-200 dark:border-border shadow-lg py-1 max-h-64 overflow-auto">
              <li className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {suggestionsLabel}
              </li>
              {suggestions.map((p, i) => (
                <li key={i}>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-muted flex items-center gap-1.5"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => select(p)}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" className="text-[#001489] shrink-0">
                      <path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                    </svg>
                    {p.prenom} {p.nom}
                    {p.rank != null && <em className="not-italic text-muted-foreground"> #{p.rank}</em>}
                    {p.club && <span className="text-muted-foreground"> · {p.club}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ── Menu filtre participants (entonnoir → choix unique) ─────────────────────────
const PFILTER_OPTIONS = [
  { value: "", label: "Aucun filtre", needsPair: false },
  { value: "avec", label: "Avec participants", needsPair: false },
  { value: "top4", label: "Top 4", needsPair: true },
  { value: "top50", label: "Top 50 %", needsPair: true },
];

function ParticipantsFilterMenu({
  value,
  onChange,
  pairReady,
}: {
  value: string;
  onChange: (v: string) => void;
  pairReady: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const active = value !== "";
  return (
    <div className="relative ml-auto" ref={ref}>
      <button
        type="button"
        className={`relative inline-flex items-center justify-center w-9 h-9 rounded-md border ${
          active
            ? "bg-[#001489]/10 border-[#001489] text-[#001489] dark-text-active"
            : "bg-gray-50 dark:bg-muted border-transparent text-muted-foreground hover:text-foreground"
        }`}
        onClick={() => setOpen((o) => !o)}
        title="Filtrer les tournois par participants"
        aria-expanded={open}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
        </svg>
        {active && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#001489]" />}
      </button>
      {open && (
        <ul className="absolute right-0 mt-1 z-20 w-52 rounded-md bg-white dark:bg-card border border-gray-200 dark:border-border shadow-lg py-1">
          {PFILTER_OPTIONS.map((opt) => {
            const disabled = opt.needsPair && !pairReady;
            return (
              <li key={opt.value}>
                <button
                  type="button"
                  className={`w-full flex items-center gap-2 text-left px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-muted disabled:opacity-40 ${
                    value === opt.value ? "text-[#001489] dark-text-active font-medium" : ""
                  }`}
                  disabled={disabled}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  title={disabled ? "Sélectionnez joueur 1 et joueur 2" : undefined}
                >
                  <span>{value === opt.value ? "●" : "○"}</span>
                  {opt.label}
                  {disabled && <span className="text-muted-foreground"> · paire requise</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ── Projection d'une paire ──────────────────────────────────────────────────────
function joueurLabel(j: any): string {
  if (!j || (!j.nom && !j.prenom)) return "Place libre";
  if (j.nom === "Anonyme") return "Anonyme";
  return [j.prenom, j.nom].filter(Boolean).join(" ");
}
const sameId = (a: any, b: any) => a != null && b != null && String(a) === String(b);
const ordinal = (n: number) => (n === 1 ? "1er" : `${n}e`);

function projectPair(epreuves: any[] | undefined, j1: Joueur | null, j2: Joueur | null): any {
  if (!epreuves?.length) return null;
  const id1 = j1?.idCrm,
    id2 = j2?.idCrm;

  if (id1 != null && id2 != null) {
    for (const ep of epreuves) {
      for (const p of ep.paires) {
        const ids = [p.joueur1?.idCrm, p.joueur2?.idCrm];
        if (ids.some((x) => sameId(x, id1)) && ids.some((x) => sameId(x, id2))) {
          return { eprId: ep.eprId, position: p.position, total: ep.paires.length, inscrit: true };
        }
      }
    }
  }
  if (id1 == null || id2 == null) {
    const id = id1 ?? id2;
    if (id == null) {
      // aucun idCrm exploitable → tenter la projection par rangs si les deux joueurs sont là
      if (!j1 || !j2) return null;
    } else {
      for (const ep of epreuves) {
        for (const p of ep.paires) {
          const ids = [p.joueur1?.idCrm, p.joueur2?.idCrm];
          if (ids.some((x) => sameId(x, id))) {
            return { eprId: ep.eprId, position: p.position, total: ep.paires.length, inscrit: true };
          }
        }
      }
      if (!j1 || !j2) return null; // un seul joueur, non inscrit → pas de projection
    }
  }

  const r1 = j1?.rank,
    r2 = j2?.rank;
  if (r1 == null || r2 == null) return null;
  const poids = r1 + r2;
  const ep = epreuves.reduce((a: any, b: any) => (b.paires.length > (a?.paires.length || 0) ? b : a), null);
  const better = ep.paires.filter((p: any) => (p.poids != null ? p.poids : Infinity) < poids).length;
  return {
    eprId: ep.eprId,
    position: better + 1,
    total: ep.paires.length + 1,
    projected: true,
    poids,
    ghost: {
      position: better + 1,
      poids,
      joueur1: { prenom: j1!.prenom, nom: j1!.nom, rang: r1 },
      joueur2: { prenom: j2!.prenom, nom: j2!.nom, rang: r2 },
    },
  };
}

// Nom de joueur cliquable → ouvre la popup "Historique des tournois" (sans empilement :
// on part d'un panneau, pas d'un modal). Non cliquable si pas d'idCrm (place libre / ghost).
function JoueurName({ j }: { j: any }) {
  const label = joueurLabel(j);
  if (!j?.idCrm) return <>{label}</>;
  return (
    <TournamentHistoryCard
      idCrm={String(j.idCrm)}
      name={label}
      trigger={
        <span className="cursor-pointer text-[#001489] dark:text-blue-400 hover:underline">
          {label}
        </span>
      }
    />
  );
}

function PaireRow({ p, index, kind }: { p: any; index: number; kind?: "ghost" | "hi" }) {
  const r1 = p.joueur1?.rang,
    r2 = p.joueur2?.rang;
  const somme = r1 != null && r2 != null ? r1 + r2 : null;
  const rowCls =
    kind === "ghost"
      ? "bg-[#001489]/10 text-[#001489] dark:text-blue-300 font-medium"
      : kind === "hi"
      ? "bg-amber-100/60 dark:bg-amber-500/10"
      : "";
  return (
    <tr className={`border-b border-gray-100 dark:border-border ${rowCls}`}>
      <td className="px-2 py-1.5 text-center tabular-nums">{p.position ?? index + 1}</td>
      <td className="px-2 py-1.5">
        <JoueurName j={p.joueur1} />
        {kind === "ghost" && <span className="text-[10px] opacity-70"> (vous)</span>}
      </td>
      <td className="px-2 py-1.5 text-right tabular-nums">{r1 != null ? `#${r1}` : "—"}</td>
      <td className="px-2 py-1.5"><JoueurName j={p.joueur2} /></td>
      <td className="px-2 py-1.5 text-right tabular-nums">{r2 != null ? `#${r2}` : "—"}</td>
      <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{somme != null ? somme : "—"}</td>
    </tr>
  );
}

function Participants({ state, proj }: { state: any; proj: any }) {
  if (!state || state.loading)
    return <div className="text-xs text-muted-foreground py-3">Chargement des inscrits…</div>;
  if (state.error)
    return <div className="text-xs text-red-600 py-3">{state.error}</div>;

  const epreuves = state.data?.epreuves ?? [];
  if (!epreuves.length) {
    return <div className="text-xs text-muted-foreground py-3">Aucune paire inscrite pour le moment.</div>;
  }

  return (
    <div className="mt-3 space-y-4">
      {epreuves.map((ep: any) => {
        const isTargetEp = proj && proj.eprId === ep.eprId;
        const ghost = isTargetEp && proj.projected ? proj.ghost : null;
        return (
          <div key={ep.eprId}>
            <div className="text-xs font-semibold text-foreground mb-1">
              {ep.libelle || ep.epreuveLabel}{" "}
              <span className="font-normal text-muted-foreground">({ep.paires.length} paires)</span>
            </div>
            <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-border">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 dark:bg-muted text-muted-foreground">
                    <th className="px-2 py-1.5 text-center font-medium">#</th>
                    <th className="px-2 py-1.5 text-left font-medium">Joueur 1</th>
                    <th className="px-2 py-1.5 text-right font-medium">Class. 1</th>
                    <th className="px-2 py-1.5 text-left font-medium">Joueur 2</th>
                    <th className="px-2 py-1.5 text-right font-medium">Class. 2</th>
                    <th className="px-2 py-1.5 text-right font-medium">Class. paire</th>
                  </tr>
                </thead>
                <tbody>
                  {ep.paires.map((p: any, i: number) => {
                    const rows = [];
                    if (ghost && ghost.position === (p.position ?? i + 1)) {
                      rows.push(<PaireRow key="ghost" p={ghost} index={i} kind="ghost" />);
                    }
                    const isHi = isTargetEp && !proj.projected && proj.position === (p.position ?? i + 1);
                    rows.push(<PaireRow key={i} p={p} index={i} kind={isHi ? "hi" : undefined} />);
                    return rows;
                  })}
                  {ghost && ghost.position > ep.paires.length && (
                    <PaireRow key="ghost-last" p={ghost} index={ep.paires.length} kind="ghost" />
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
      {state.data?.scrapedAt && (
        <div className="text-[10px] text-muted-foreground">
          Mis à jour le{" "}
          {new Date(state.data.scrapedAt).toLocaleString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
      )}
    </div>
  );
}

// ── Barème des points (chargé à chaud depuis le serveur, éditable sans rebuild) ──
let baremesCache: any = null;
let baremesPromise: Promise<any> | null = null;
function loadBaremes(): Promise<any> {
  if (baremesCache) return Promise.resolve(baremesCache);
  if (!baremesPromise) {
    baremesPromise = fetch(resolveUrl("/api/tournois/baremes"))
      .then((r) => {
        if (!r.ok) throw new Error(`Erreur ${r.status}`);
        return r.json();
      })
      .then((d) => {
        baremesCache = d;
        return d;
      })
      .catch((e) => {
        baremesPromise = null;
        throw e;
      });
  }
  return baremesPromise;
}

// Sélectionne la colonne (tranche de nb de paires) ; P500 : colonne forcée.
function pickBinIndex(cat: any, count: number): number {
  if (cat.forceBinIndex != null) return cat.forceBinIndex;
  const bins: [number, number | null][] = cat.bins;
  for (let i = 0; i < bins.length; i++) {
    const [min, max] = bins[i];
    if (count >= min && (max == null || count <= max)) return i;
  }
  return count < bins[0][0] ? 0 : bins.length - 1;
}

const rankLabel = (i: number, total: number) => (i === total - 1 ? "32+" : String(i + 1));

function BaremeEpreuve({ cat, count, libelle, highlightRank }: { cat: any; count: number; libelle: string; highlightRank?: number | null }) {
  const binIndex = pickBinIndex(cat, count);
  const rows: number[][] = cat.rows;
  const total = rows.length;
  // Ne garde que les rangs qui rapportent des points dans cette colonne
  const scored = rows
    .map((r, i) => ({ rank: i, pts: r[binIndex] }))
    .filter((x) => x.pts > 0);

  return (
    <div>
      <div className="text-xs font-semibold text-foreground mb-1">
        {libelle}{" "}
        <span className="font-normal text-muted-foreground">
          ({count} paire{count > 1 ? "s" : ""} → {cat.binLabels[binIndex]} paires{cat.forceBinIndex != null ? ", barème fixe" : ""})
        </span>
      </div>
      <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-border">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 dark:bg-muted text-muted-foreground">
              <th className="px-2 py-1.5 text-center font-medium">Rang</th>
              <th className="px-2 py-1.5 text-right font-medium">Points</th>
            </tr>
          </thead>
          <tbody>
            {scored.map((x) => {
              const isHi = highlightRank != null && highlightRank === x.rank + 1;
              return (
                <tr
                  key={x.rank}
                  className={`border-b border-gray-100 dark:border-border ${
                    isHi ? "bg-emerald-100/60 dark:bg-emerald-500/10 font-semibold" : ""
                  }`}
                >
                  <td className="px-2 py-1.5 text-center tabular-nums">{rankLabel(x.rank, total)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{x.pts}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BaremePanel({ categorie, baremes, pState, proj }: { categorie: string; baremes: any; pState: any; proj: any }) {
  const cat = baremes?.categories?.[categorie];
  if (!cat) return <div className="text-xs text-muted-foreground py-3">Barème non disponible pour {categorie}.</div>;

  if (!pState || pState.loading)
    return <div className="text-xs text-muted-foreground py-3">Chargement des inscrits…</div>;

  const epreuves = pState.data?.epreuves ?? [];
  if (!epreuves.length)
    return <div className="text-xs text-muted-foreground py-3">Aucune paire inscrite : impossible de déterminer la colonne du barème.</div>;

  return (
    <div className="mt-3 space-y-4">
      {epreuves.map((ep: any) => {
        const hi = proj && proj.eprId === ep.eprId ? proj.position : null;
        return (
          <BaremeEpreuve
            key={ep.eprId}
            cat={cat}
            count={ep.paires.length}
            libelle={ep.libelle || ep.epreuveLabel || ""}
            highlightRank={hi}
          />
        );
      })}
      {baremes.version && (
        <div className="text-[10px] text-muted-foreground">Barème FFT v{baremes.version}</div>
      )}
    </div>
  );
}

const CRENEAU_ICONS: Record<string, string> = { matin: "☕", apresMidi: "🌤️", soiree: "🌙", journee: "🗓️" };
const CRENEAUX = Object.keys(CRENEAU_ICONS);

// ── Card ──────────────────────────────────────────────────────────────────────
// "2026-10-22" → "jeu 22 oct" (fr) / "Thu, Oct 22" (en) ; repli sur date_str brute si dateIso absent.
function formatTournoiDate(t: { date?: string; dateIso?: string }, lang: string): string {
  if (!t.dateIso) return t.date ?? "";
  const [y, m, d] = t.dateIso.split("-").map(Number);
  return new Date(y, m - 1, d)
    .toLocaleDateString(lang, { weekday: "short", day: "numeric", month: "short" })
    .replace(/\./g, "");
}

export function TournoiCard({
  t,
  joueur1 = null,
  joueur2 = null,
  open,
  onOpenChange,
}: {
  t: any;
  joueur1?: Joueur | null;
  joueur2?: Joueur | null;
  open: boolean;
  onOpenChange?: (next: boolean) => void;
}) {
  const { t: tr, i18n } = useTranslation();
  const places: number[] = [...new Set<number>(t.places ?? [])];
  // Plusieurs épreuves → une ligne par épreuve (créneau + places) au lieu des valeurs globales
  const details: { label: string; creneau: string | null; places: number | null }[] =
    (t.epreuvesDetail ?? []).length > 1 ? t.epreuvesDetail : [];
  const detailsHaveCreneau = details.some((d) => d.creneau);
  const [showInscription, setShowInscription] = useState(false);
  const hasContact = !!(t.contactTel || t.contactMail);
  const inscription =
    hasContact && joueur1 && joueur2
      ? buildInscriptionMessage({
          categorie: t.categorie,
          dateIso: t.dateIso,
          dateFallback: t.date,
          j1: joueur1,
          j2: joueur2,
        })
      : null;
  const [pState, setPState] = useState<any>(null);
  const [showBareme, setShowBareme] = useState(false);
  const [baremes, setBaremes] = useState<any>(baremesCache);
  const color = CAT_COLORS[t.categorie] ?? "#64748b";

  useEffect(() => {
    if (!baremes) loadBaremes().then(setBaremes).catch(() => {});
  }, [baremes]);
  const baremeAvailable = !!baremes?.categories?.[t.categorie];

  const fetchParticipants = useCallback(() => {
    if (!t.idHomologation) return;
    setPState((prev: any) => {
      if (prev) return prev;
      fetch(resolveUrl(`/api/tournois/${encodeURIComponent(t.idHomologation)}/participants`))
        .then((r) => {
          if (!r.ok) throw new Error(`Erreur ${r.status}`);
          return r.json();
        })
        .then((data) => setPState({ loading: false, error: null, data }))
        .catch((err) => setPState({ loading: false, error: err.message, data: null }));
      return { loading: true, error: null, data: null };
    });
  }, [t.idHomologation]);

  useEffect(() => {
    setPState(null);
  }, [t.idHomologation]);

  useEffect(() => {
    if (joueur1 || joueur2) fetchParticipants();
  }, [t.idHomologation, joueur1, joueur2, fetchParticipants]);

  const proj = useMemo(() => projectPair(pState?.data?.epreuves, joueur1, joueur2), [pState, joueur1, joueur2]);

  const showParticipants = !!open;

  function toggleParticipants() {
    fetchParticipants();
    if (!showParticipants) setShowBareme(false); // exclusif : ferme le barème à l'ouverture
    onOpenChange?.(!showParticipants);
  }

  function onCardClick(e: React.MouseEvent) {
    if (!proj) return;
    if ((e.target as HTMLElement).closest("a, button")) return;
    fetchParticipants();
    setShowBareme(false);
    onOpenChange?.(true);
  }

  return (
    <div
      className={`rounded-lg bg-white dark:bg-card border border-gray-200 dark:border-border p-4 shadow-sm ${
        proj ? "cursor-pointer hover:border-[#001489]/40" : ""
      }`}
      onClick={proj ? onCardClick : undefined}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <span className="text-xs font-semibold text-muted-foreground">
          {formatTournoiDate(t, i18n.language)}
          {places.length > 0 && details.length === 0 && (
            <> · {tr("players.tab.tournamentPlaces", { count: Math.max(...places), places: places.join(" / ") })}</>
          )}
        </span>
        <div className="flex flex-wrap gap-1 justify-end">
          {t.categorie && (
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-full border"
              style={{ background: color + "22", color, borderColor: color + "55" }}
            >
              {t.categorie}
            </span>
          )}
          {(detailsHaveCreneau ? [] : t.creneaux ?? []).map((c: string) => (
            <span
              key={c}
              className={`text-[10px] font-medium px-2 py-0.5 rounded-full border border-transparent ${
                c === "soiree"
                  ? "bg-indigo-100 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300"
                  : "bg-sky-100 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300"
              }`}
            >
              {CRENEAU_ICONS[c]} {tr(`players.tab.creneau.${c}`)}
            </span>
          ))}
          {t.epreuve && (
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 dark:bg-muted text-muted-foreground border border-transparent">
              {t.epreuve}
            </span>
          )}
          {(t.categorieAge || "")
            .split(",")
            .map((a: string) => a.trim())
            .filter((a: string) => a && a !== "Senior")
            .map((a: string) => (
              <span
                key={a}
                className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-transparent"
              >
                {a}
              </span>
            ))}
        </div>
      </div>
      <h3 className="font-bold text-foreground text-sm mb-2">
        {t.tournoiUrl ? (
          <a href={t.tournoiUrl} target="_blank" rel="noopener noreferrer" className="text-[#001489] dark-text-active hover:underline">
            {t.name}
          </a>
        ) : (
          t.name
        )}
      </h3>
      {details.length > 0 && (
        <div className="mb-2 space-y-0.5">
          {details.map((d, i) => (
            <div key={i} className="text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">{d.label}</span>
              {d.creneau && <> · {CRENEAU_ICONS[d.creneau]} {tr(`players.tab.creneau.${d.creneau}`)}</>}
              {d.places && <> · {tr("players.tab.tournamentPlaces", { count: d.places, places: d.places })}</>}
            </div>
          ))}
        </div>
      )}
      <div className="space-y-1 text-xs text-muted-foreground">
        {t.club && (
          <div className="flex items-center gap-1.5">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
            {t.clubUrl ? (
              <a href={t.clubUrl} target="_blank" rel="noopener noreferrer" className="text-[#001489] dark-text-active hover:underline">
                {t.club}
              </a>
            ) : (
              <span>{t.club}</span>
            )}
          </div>
        )}
        {(t.adresse || t.ville) && (
          <div className="flex items-center gap-1.5">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            <span>{t.adresse || t.ville}</span>
          </div>
        )}
        {t.arbitre && t.arbitre !== "-" && (
          <div className="flex items-center gap-1.5 opacity-70">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="8" r="4" />
              <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
            </svg>
            <span>Arbitre : {t.arbitre}</span>
          </div>
        )}
      </div>
      {t.idHomologation && (
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <button
            type="button"
            className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 h-8 rounded-md border ${
              showParticipants
                ? "bg-[#001489]/10 border-[#001489] text-[#001489] dark-text-active"
                : "bg-gray-50 dark:bg-muted border-transparent text-foreground hover:border-gray-300 dark:hover:border-border"
            }`}
            onClick={toggleParticipants}
            aria-expanded={showParticipants}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            Participants
            <span className="text-[10px]">{showParticipants ? "▲" : "▼"}</span>
          </button>
          {baremeAvailable && (
            <button
              type="button"
              className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 h-8 rounded-md border ${
                showBareme
                  ? "bg-[#001489]/10 border-[#001489] text-[#001489] dark-text-active"
                  : "bg-gray-50 dark:bg-muted border-transparent text-foreground hover:border-gray-300 dark:hover:border-border"
              }`}
              onClick={() => {
                fetchParticipants();
                if (!showBareme) onOpenChange?.(false); // exclusif : ferme les participants à l'ouverture
                setShowBareme((v) => !v);
              }}
              aria-expanded={showBareme}
              title="Points à gagner selon la catégorie et le nombre de paires inscrites"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 21V10M10 21V4M16 21v-7M22 21H2" />
              </svg>
              Barème
              <span className="text-[10px]">{showBareme ? "▲" : "▼"}</span>
            </button>
          )}
          {hasContact && (
            <button
              type="button"
              className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 h-8 rounded-md border ${
                showInscription
                  ? "bg-[#001489]/10 border-[#001489] text-[#001489] dark-text-active"
                  : "bg-gray-50 dark:bg-muted border-transparent text-foreground hover:border-gray-300 dark:hover:border-border"
              }`}
              onClick={() => setShowInscription((v) => !v)}
              aria-expanded={showInscription}
            >
              ✉️ {tr("players.tab.inscription.button")}
              <span className="text-[10px]">{showInscription ? "▲" : "▼"}</span>
            </button>
          )}
          {proj && (
            <span
              className={`text-xs font-semibold px-2 py-1 rounded-md ${
                proj.projected
                  ? "bg-[#001489]/10 text-[#001489] dark:text-blue-300"
                  : "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
              }`}
              title={proj.projected ? "Position projetée de la paire (non inscrite)" : "Classement provisoire de la paire inscrite"}
            >
              {proj.projected && "≈ "}
              {ordinal(proj.position)} / {proj.total}
            </span>
          )}
        </div>
      )}
      {showParticipants && t.idHomologation && <Participants state={pState} proj={proj} />}
      {showBareme && t.idHomologation && <BaremePanel categorie={t.categorie} baremes={baremes} pState={pState} proj={proj} />}
      {showInscription && hasContact && (
        <div className="mt-2 rounded-md bg-gray-50 dark:bg-muted p-3 text-xs" onClick={(e) => e.stopPropagation()}>
          {inscription ? (
            <>
              <div className="flex flex-wrap gap-2">
                {t.contactTel && (
                  <a
                    href={smsHref(t.contactTel, inscription.body, Capacitor.getPlatform())}
                    className="inline-flex items-center gap-1.5 font-semibold px-3 h-8 rounded-md bg-[#001489] text-white hover:bg-[#001489]/90"
                  >
                    📱 {tr("players.tab.inscription.sms")}
                  </a>
                )}
                {t.contactMail && (
                  <a
                    href={mailHref(t.contactMail, inscription.subject, inscription.body)}
                    className="inline-flex items-center gap-1.5 font-semibold px-3 h-8 rounded-md bg-[#001489] text-white hover:bg-[#001489]/90"
                  >
                    ✉️ {tr("players.tab.inscription.email")}
                  </a>
                )}
              </div>
              <pre className="mt-2 whitespace-pre-wrap font-sans text-muted-foreground">{inscription.body}</pre>
            </>
          ) : (
            <span className="text-muted-foreground">{tr("players.tab.inscription.needPlayers")}</span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Calendrier ──────────────────────────────────────────────────────────────────
const JOURS_SEMAINE = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const MOIS_LABELS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];
function pad(n: number) {
  return String(n).padStart(2, "0");
}
function toKey(y: number, m: number, d: number) {
  return `${y}-${pad(m)}-${pad(d)}`;
}
function buildMonthGrid(year: number, month: number) {
  const monthIndex = month - 1;
  const firstOfMonth = new Date(year, monthIndex, 1);
  const jsFirstDow = firstOfMonth.getDay();
  const leadingDays = (jsFirstDow + 6) % 7;
  const daysInMonth = new Date(year, month, 0).getDate();
  const totalCells = Math.ceil((leadingDays + daysInMonth) / 7) * 7;
  const cells = [];
  const gridStart = new Date(year, monthIndex, 1 - leadingDays);
  for (let i = 0; i < totalCells; i++) {
    const d = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
    cells.push({
      key: toKey(d.getFullYear(), d.getMonth() + 1, d.getDate()),
      day: d.getDate(),
      inMonth: d.getMonth() === monthIndex && d.getFullYear() === year,
    });
  }
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

function TournoisCalendrier({ appliedFilters, joueur1, joueur2 }: { appliedFilters: any; joueur1?: Joueur | null; joueur2?: Joueur | null }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [calData, setCalData] = useState<any>(null);
  const [calLoading, setCalLoading] = useState(false);
  const [calError, setCalError] = useState<string | null>(null);
  const [expandedDate, setExpandedDate] = useState<string | null>(null);
  const [expandedResult, setExpandedResult] = useState<any>(null);
  const [expandedLoading, setExpandedLoading] = useState(false);
  const [openTournoi, setOpenTournoi] = useState<string | null>(null);

  const todayKey = toKey(now.getFullYear(), now.getMonth() + 1, now.getDate());

  const fetchCalendar = useCallback(async (y: number, m: number, filters: any) => {
    setCalLoading(true);
    setCalError(null);
    try {
      const params = new URLSearchParams({ year: String(y), month: String(m) });
      if (filters.geo) {
        params.set("lat", filters.geo.lat);
        params.set("lng", filters.geo.lng);
        params.set("rayon", filters.rayon);
      }
      if (filters.cat !== null && filters.cat.length > 0) params.set("categorie", filters.cat.join(","));
      if (filters.ep && filters.ep.length > 0) params.set("epreuve", filters.ep.join(","));
      if (filters.age && filters.age.length > 0) params.set("categorieAge", filters.age.join(","));
      if (filters.cl !== null && filters.cl?.length > 0) params.set("clubs", filters.cl.join(","));
      if (filters.cr?.length > 0 && filters.cr.length < CRENEAUX.length) params.set("creneau", filters.cr.join(","));
      if (filters.pf) {
        params.set("pFilter", filters.pf);
        if ((filters.pf === "top4" || filters.pf === "top50") && filters.poids != null) params.set("poids", String(filters.poids));
      }
      const res = await fetch(resolveUrl(`/api/tournois/calendrier?${params}`));
      if (!res.ok) throw new Error(`Erreur serveur ${res.status}`);
      setCalData(await res.json());
    } catch (err: any) {
      setCalError(err.message);
    } finally {
      setCalLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!appliedFilters) return;
    setExpandedDate(null);
    fetchCalendar(year, month, appliedFilters);
  }, [year, month, appliedFilters, fetchCalendar]);

  function goToMonth(y: number, m: number) {
    if (m < 1) {
      setYear(y - 1);
      setMonth(12);
    } else if (m > 12) {
      setYear(y + 1);
      setMonth(1);
    } else {
      setYear(y);
      setMonth(m);
    }
  }
  function goToday() {
    setYear(now.getFullYear());
    setMonth(now.getMonth() + 1);
  }

  function toggleExpand(dateKey: string) {
    if (expandedDate === dateKey) {
      setExpandedDate(null);
      return;
    }
    setExpandedDate(dateKey);
    setExpandedLoading(true);
    const params = new URLSearchParams({ page: "1", date: dateKey });
    if (appliedFilters.geo) {
      params.set("lat", appliedFilters.geo.lat);
      params.set("lng", appliedFilters.geo.lng);
      params.set("rayon", appliedFilters.rayon);
    }
    if (appliedFilters.cat !== null && appliedFilters.cat.length > 0) params.set("categorie", appliedFilters.cat.join(","));
    if (appliedFilters.ep && appliedFilters.ep.length > 0) params.set("epreuve", appliedFilters.ep.join(","));
    if (appliedFilters.age && appliedFilters.age.length > 0) params.set("categorieAge", appliedFilters.age.join(","));
    if (appliedFilters.cl !== null && appliedFilters.cl?.length > 0) params.set("clubs", appliedFilters.cl.join(","));
    if (appliedFilters.cr?.length > 0 && appliedFilters.cr.length < CRENEAUX.length) params.set("creneau", appliedFilters.cr.join(","));
    if (appliedFilters.pf) {
      params.set("pFilter", appliedFilters.pf);
      if ((appliedFilters.pf === "top4" || appliedFilters.pf === "top50") && appliedFilters.poids != null) params.set("poids", String(appliedFilters.poids));
    }
    fetch(resolveUrl(`/api/tournois?${params}`))
      .then((r) => r.json())
      .then(setExpandedResult)
      .finally(() => setExpandedLoading(false));
  }

  if (!appliedFilters) {
    return (
      <div className="text-xs text-muted-foreground text-center py-8">
        Choisis tes filtres puis clique sur "Rechercher" pour afficher le calendrier.
      </div>
    );
  }

  const weeks = buildMonthGrid(year, month);
  const days = calData?.days ?? {};

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <button type="button" className="w-8 h-8 rounded-md bg-gray-50 dark:bg-muted hover:bg-gray-100 dark:hover:bg-muted/70" onClick={() => goToMonth(year, month - 1)}>
          ◄
        </button>
        <span className="font-semibold text-foreground text-sm min-w-32 text-center">
          {MOIS_LABELS[month - 1]} {year}
        </span>
        <button type="button" className="w-8 h-8 rounded-md bg-gray-50 dark:bg-muted hover:bg-gray-100 dark:hover:bg-muted/70" onClick={() => goToMonth(year, month + 1)}>
          ►
        </button>
        <button type="button" className="ml-auto text-xs px-3 h-8 rounded-md bg-gray-50 dark:bg-muted hover:bg-gray-100 dark:hover:bg-muted/70" onClick={goToday}>
          Aujourd'hui
        </button>
      </div>

      {calError && <div className="text-xs text-red-600 mb-2">{calError}</div>}
      {calLoading && <div className="text-xs text-muted-foreground mb-2">Chargement…</div>}

      <div className="grid grid-cols-7 gap-1 text-[10px] font-semibold text-muted-foreground text-center mb-1">
        {JOURS_SEMAINE.map((j) => (
          <div key={j}>{j}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {weeks.map((week) =>
          week.map((cell) => {
            const info = cell.inMonth ? days[cell.key] : null;
            const isToday = cell.key === todayKey;
            return (
              <div
                key={cell.key}
                className={`min-h-16 rounded-md border p-1 flex flex-col gap-0.5 ${
                  !cell.inMonth
                    ? "opacity-40 border-transparent"
                    : "border-gray-200 dark:border-border bg-white dark:bg-card"
                } ${isToday ? "ring-1 ring-[#001489]" : ""} ${info && info.count > 0 ? "cursor-pointer hover:border-[#001489]/50" : ""}`}
                onClick={info && info.count > 0 ? () => toggleExpand(cell.key) : undefined}
              >
                <span className="text-[10px] font-semibold text-muted-foreground">{cell.day}</span>
                {info &&
                  info.preview.slice(0, 3).map((p: any, i: number) => {
                    const color = CAT_COLORS[p.categorie] ?? "#64748b";
                    return (
                      <span
                        key={i}
                        className="text-[9px] leading-tight px-1 py-0.5 rounded border truncate"
                        style={{ background: color + "22", color, borderColor: color + "55" }}
                        title={p.categorie ? `${p.categorie} — ${p.name}` : p.name}
                      >
                        {p.name}
                      </span>
                    );
                  })}
                {info && info.count > 3 && <span className="text-[9px] text-muted-foreground">+{info.count - 3} autres</span>}
              </div>
            );
          })
        )}
      </div>

      {expandedDate && (
        <div className="mt-4 rounded-lg border border-gray-200 dark:border-border p-3">
          <div className="flex items-center justify-between mb-2">
            <strong className="text-sm">{expandedDate}</strong>
            <button type="button" className="w-7 h-7 rounded-md bg-gray-50 dark:bg-muted" onClick={() => setExpandedDate(null)}>
              ×
            </button>
          </div>
          {expandedLoading && <div className="text-xs text-muted-foreground">Chargement…</div>}
          {expandedResult && (
            <div className="space-y-3">
              {expandedResult.tournois.map((t: any, i: number) => (
                <TournoiCard
                  key={t.idHomologation ?? `idx-${i}`}
                  t={t}
                  joueur1={joueur1}
                  joueur2={joueur2}
                  open={openTournoi === (t.idHomologation ?? `idx-${i}`)}
                  onOpenChange={(next) => setOpenTournoi(next ? t.idHomologation ?? `idx-${i}` : null)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Découpe "Prénom NOM" en {prenom, nom} ────────────────────────────────────────
function splitName(name: string): { prenom: string; nom: string } {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return { prenom: parts[0], nom: "" };
  return { prenom: parts[0], nom: parts.slice(1).join(" ") };
}

// ── Vue principale Tournois ──────────────────────────────────────────────────────
export default function TournoisView({ playerId }: { playerId?: string | null }) {
  const { t: tr } = useTranslation();
  // Fiche du joueur loggé (name + fftRank) → rang FFT lu frais, suit la maj mensuelle
  const { data: selfFiche } = useQuery<{ name?: string; fftRank?: number | null; fftLicence?: string | null }>({
    queryKey: ["/api/players", playerId],
    enabled: !!playerId,
  });
  const selfPlayer = useMemo<Joueur | null>(() => {
    if (!selfFiche || !selfFiche.name) return null;
    const { prenom, nom } = splitName(selfFiche.name);
    return { prenom, nom, rank: selfFiche.fftRank ?? null, idCrm: null, licence: selfFiche.fftLicence || undefined };
  }, [selfFiche]);

  // Joueurs favoris PadELO ayant un classement padel (rang FFT) → suggérés dans "Partenaire"
  const { data: rankingPlayers = [] } = useQuery<any[]>({
    queryKey: ["/api/players/rankings?timeFilter=all"],
  });
  const favoriteJoueurs = useMemo<Joueur[]>(() => {
    const key = playerId ? `followedPlayers_${playerId}` : "followedPlayers";
    let ids: string[] = [];
    try {
      ids = JSON.parse(localStorage.getItem(key) || "[]");
    } catch {
      ids = [];
    }
    const idSet = new Set(ids);
    return (rankingPlayers as any[])
      .filter((p) => idSet.has(p.id) && p.fftRank != null && p.id !== playerId)
      .map((p) => {
        const { prenom, nom } = splitName(p.name || "");
        return { prenom, nom, rank: p.fftRank as number, idCrm: null, licence: p.fftLicence || undefined };
      })
      .sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity));
  }, [rankingPlayers, playerId]);

  const [geo, setGeo] = useLocalState<Geo | null>("padelo_t_geo", null);
  const [rayon, setRayon] = useLocalState<number>("padelo_t_rayon", 30);
  const [categorieFilter, setCategorieFilter] = useLocalState<string[] | null>("padelo_t_cat", null);
  const [epreuve, setEpreuve] = useLocalState<string[]>("padelo_t_epreuve", ["DM"]);
  const [ageFilter, setAgeFilter] = useLocalState<string[]>("padelo_t_age", ["Senior"]);
  const [creneauFilter, setCreneauFilter] = useLocalState<string[]>("padelo_t_creneau", CRENEAUX);
  const [clubFilter, setClubFilter] = useState<string[] | null>(null);
  const [clubOptions, setClubOptions] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useLocalState<"liste" | "calendrier">("padelo_t_view", "liste");
  const [appliedFilters, setAppliedFilters] = useLocalState<any>("padelo_t_applied", null);
  const [joueur1, setJoueur1] = useLocalState<Joueur | null>("padelo_t_j1", null);
  const [joueur2, setJoueur2] = useLocalState<Joueur | null>("padelo_t_j2", null);
  const [participantsFilter, setParticipantsFilter] = useLocalState<string>("padelo_t_pf", "");
  const [expertOpen, setExpertOpen] = useLocalState<boolean>("padelo_t_expert", false);
  const [openTournoi, setOpenTournoi] = useState<string | null>(null);

  // Joueur sans licence (mémorisé avant son ajout, ou favori PadELO sans fftLicence) : on la cherche
  // dans la base FFT — par idCrm, sinon par nom s'il est unique. Une tentative par joueur et par session.
  const licenceTried = useRef(new Set<string>());
  useEffect(() => {
    const pairs: [Joueur | null, (j: Joueur | null) => void][] = [[joueur1, setJoueur1], [joueur2, setJoueur2]];
    for (const [j, setJ] of pairs) {
      const key = j ? `${j.idCrm ?? ""}|${j.prenom}|${j.nom}` : "";
      if (!j || j.licence || licenceTried.current.has(key)) continue;
      licenceTried.current.add(key);
      fetch(resolveUrl(`/api/players/padel?q=${encodeURIComponent(`${j.prenom} ${j.nom}`)}&page=1`))
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          const players: Joueur[] = data?.players ?? [];
          const same = (a: string, b: string) => a.localeCompare(b, "fr", { sensitivity: "base" }) === 0;
          const byName = players.filter((p) => same(p.prenom, j.prenom) && same(p.nom, j.nom));
          const found = j.idCrm ? players.find((p) => p.idCrm === j.idCrm) : byName.length === 1 ? byName[0] : null;
          if (found?.licence) setJ({ ...j, licence: found.licence });
        })
        .catch(() => {});
    }
  }, [joueur1, joueur2, setJoueur1, setJoueur2]);

  const pairPoids = joueur1?.rank != null && joueur2?.rank != null ? joueur1.rank + joueur2.rank : null;

  // Pré-remplit "Moi" avec le joueur loggé (rang toujours frais) ; sinon vide
  const selfInit = useRef(false);
  useEffect(() => {
    if (!selfPlayer || selfInit.current) return;
    selfInit.current = true;
    if (joueur1 == null) {
      setJoueur1(selfPlayer);
    } else if (
      joueur1.prenom === selfPlayer.prenom &&
      joueur1.nom === selfPlayer.nom &&
      (joueur1.rank !== selfPlayer.rank || (!joueur1.licence && selfPlayer.licence))
    ) {
      // même joueur, rang ou licence périmés → resync depuis la fiche
      setJoueur1({ ...joueur1, rank: selfPlayer.rank, idCrm: null, licence: selfPlayer.licence ?? joueur1.licence });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selfPlayer]);

  useEffect(() => {
    setClubFilter(null);
    if (!geo) {
      setClubOptions([]);
      return;
    }
    fetch(resolveUrl(`/api/tournois/clubs?lat=${geo.lat}&lng=${geo.lng}&rayon=${rayon}`))
      .then((r) => r.json())
      .then((data) => setClubOptions(Array.isArray(data) ? data : []))
      .catch(() => setClubOptions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geo, rayon]);

  const didInit = useRef(false);
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    if (appliedFilters) {
      fetchTournois(1, geo, rayon, categorieFilter, epreuve, clubFilter, participantsFilter, ageFilter, pairPoids, creneauFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clubDropdownOptions = useMemo(() => clubOptions.map((c) => ({ name: c })), [clubOptions]);
  const categorieDropdownOptions = useMemo(() => CATEGORIES.map((c) => ({ name: c })), []);
  const ageDropdownOptions = useMemo(() => AGE_CATEGORIES.map((a) => ({ name: a })), []);
  const creneauDropdownOptions = useMemo(() => CRENEAUX.map((c) => ({ name: c })), []);
  const epreuveDropdownOptions = useMemo(() => EPREUVES.map((e) => ({ name: e.value })), []);
  const epreuveLabelFor = (name: string) => EPREUVES.find((e) => e.value === name)?.label ?? name;

  const advancedChips = useMemo(() => {
    const chips: string[] = [];
    if (geo) chips.push(`📍 ${geo.label} · ${rayon} km`);
    if (joueur1) chips.push(`Moi : ${joueur1.prenom} ${joueur1.nom}`);
    if (epreuve && epreuve.length > 0 && epreuve.length < EPREUVES.length) {
      chips.push(epreuve.map(epreuveLabelFor).join(", "));
    }
    if (ageFilter && ageFilter.length > 0 && !(ageFilter.length === 1 && ageFilter[0] === "Senior")) {
      chips.push(ageFilter.join(", "));
    }
    if (clubFilter && clubFilter.length > 0) chips.push(`Club : ${clubFilter.join(", ")}`);
    if (creneauFilter.length > 0 && creneauFilter.length < CRENEAUX.length) {
      chips.push(creneauFilter.map((c) => `${CRENEAU_ICONS[c]} ${tr(`players.tab.creneau.${c}`)}`).join(", "));
    }
    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geo, rayon, joueur1, epreuve, ageFilter, clubFilter, creneauFilter, tr]);

  const fetchTournois = useCallback(
    async (
      p: number,
      geoArg: Geo | null,
      rayonArg: number,
      cat: string[] | null,
      ep: string[],
      cl: string[] | null,
      pf: string,
      age: string[],
      poids: number | null,
      cr: string[]
    ) => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ page: String(p) });
        if (geoArg) {
          params.set("lat", String(geoArg.lat));
          params.set("lng", String(geoArg.lng));
          params.set("rayon", String(rayonArg));
        }
        if (cat !== null && cat.length > 0) params.set("categorie", cat.join(","));
        if (ep && ep.length > 0) params.set("epreuve", ep.join(","));
        if (cl !== null && cl.length > 0) params.set("clubs", cl.join(","));
        if (pf) {
          params.set("pFilter", pf);
          if ((pf === "top4" || pf === "top50") && poids != null) params.set("poids", String(poids));
        }
        if (age && age.length > 0) params.set("categorieAge", age.join(","));
        if (cr.length > 0 && cr.length < CRENEAUX.length) params.set("creneau", cr.join(","));
        const res = await fetch(resolveUrl(`/api/tournois?${params}`));
        if (!res.ok) throw new Error(`Erreur serveur ${res.status}`);
        setResult(await res.json());
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setExpertOpen(false);
    setPage(1);
    setAppliedFilters({ geo, rayon, cat: categorieFilter, ep: epreuve, cl: clubFilter, age: ageFilter, pf: participantsFilter, poids: pairPoids, cr: creneauFilter });
    fetchTournois(1, geo, rayon, categorieFilter, epreuve, clubFilter, participantsFilter, ageFilter, pairPoids, creneauFilter);
  }

  function goPage(p: number) {
    setPage(p);
    fetchTournois(p, geo, rayon, categorieFilter, epreuve, clubFilter, participantsFilter, ageFilter, pairPoids, creneauFilter);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function changeParticipantsFilter(next: string) {
    setParticipantsFilter(next);
    setPage(1);
    if (appliedFilters) {
      // maj appliedFilters → le calendrier se rafraîchit ; refetch la liste
      setAppliedFilters({ ...appliedFilters, pf: next, poids: pairPoids });
      fetchTournois(1, geo, rayon, categorieFilter, epreuve, clubFilter, next, ageFilter, pairPoids, creneauFilter);
    }
  }

  const btnPrimary =
    "inline-flex items-center justify-center px-4 h-9 rounded-md bg-[#001489] text-white text-xs font-semibold hover:bg-[#001489]/90 disabled:opacity-50";

  return (
    <div className="p-4 space-y-4">
      <form className="bg-white dark:bg-card rounded-lg border border-gray-200 dark:border-border p-4 space-y-3" onSubmit={handleSearch}>
        {/* Vue simple */}
        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
          <div>
            <span className="block text-[11px] font-semibold text-muted-foreground mb-1">Catégorie</span>
            <MultiDropdown
              clubs={categorieDropdownOptions}
              selected={categorieFilter}
              onChange={setCategorieFilter}
              allLabel={`Toutes les catégories (${CATEGORIES.length})`}
              noneLabel="Aucune catégorie"
              selectedLabel={(n) => `${n} catégorie${n > 1 ? "s" : ""} sélectionnée${n > 1 ? "s" : ""}`}
            />
          </div>
          <PlayerPicker label="Partenaire" value={joueur2} onChange={setJoueur2} suggestions={favoriteJoueurs} />
          <button type="submit" className={btnPrimary} disabled={loading}>
            {loading ? "Chargement…" : "Rechercher"}
          </button>
        </div>

        <button
          type="button"
          className="text-xs font-medium text-[#001489] dark-text-active hover:underline"
          onClick={() => setExpertOpen(!expertOpen)}
          aria-expanded={expertOpen}
        >
          Filtres avancés {expertOpen ? "▲" : "▼"}
        </button>

        {!expertOpen && advancedChips.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {advancedChips.map((c, i) => (
              <button
                key={i}
                type="button"
                className="text-[11px] px-2 py-1 rounded-full bg-gray-100 dark:bg-muted text-muted-foreground hover:bg-gray-200 dark:hover:bg-muted/70"
                onClick={() => setExpertOpen(true)}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {expertOpen && (
          <div className="space-y-3 pt-2 border-t border-gray-100 dark:border-border">
            <LocationBar geo={geo} onGeoChange={setGeo} rayon={rayon} onRayonChange={setRayon} />
            {geo && clubOptions.length > 0 && (
              <div>
                <span className="block text-[11px] font-semibold text-muted-foreground mb-1">Club</span>
                <MultiDropdown clubs={clubDropdownOptions} selected={clubFilter} onChange={setClubFilter} />
              </div>
            )}
            <PlayerPicker label="Moi" value={joueur1} onChange={setJoueur1} />
            <div>
              <span className="block text-[11px] font-semibold text-muted-foreground mb-1">Épreuve</span>
              <MultiDropdown
                clubs={epreuveDropdownOptions}
                selected={epreuve}
                onChange={(v) => setEpreuve(v ?? EPREUVES.map((e) => e.value))}
                allLabel={`Toutes les épreuves (${EPREUVES.length})`}
                noneLabel="Aucune épreuve"
                selectedLabel={(n) => `${n} épreuve${n > 1 ? "s" : ""}`}
                renderLabel={(c) => epreuveLabelFor(c.name)}
              />
            </div>
            <div>
              <span className="block text-[11px] font-semibold text-muted-foreground mb-1">Catégorie d'âge</span>
              <MultiDropdown
                clubs={ageDropdownOptions}
                selected={ageFilter}
                onChange={(v) => setAgeFilter(v ?? AGE_CATEGORIES)}
                allLabel={`Toutes les catégories d'âge (${AGE_CATEGORIES.length})`}
                noneLabel="Aucune catégorie d'âge"
                selectedLabel={(n) => `${n} catégorie${n > 1 ? "s" : ""} d'âge`}
              />
            </div>
            <div>
              <span className="block text-[11px] font-semibold text-muted-foreground mb-1">{tr("players.tab.creneau.label")}</span>
              <MultiDropdown
                clubs={creneauDropdownOptions}
                selected={creneauFilter}
                onChange={(v) => setCreneauFilter(v ?? CRENEAUX)}
                allLabel={tr("players.tab.creneau.all")}
                noneLabel={tr("players.tab.creneau.none")}
                selectedLabel={(n) => tr("players.tab.creneau.selected", { count: n })}
                renderLabel={(c) => `${CRENEAU_ICONS[c.name]} ${tr(`players.tab.creneau.${c.name}`)}`}
              />
            </div>
          </div>
        )}
      </form>

      <div className="flex items-center gap-2">
        <button
          type="button"
          className={`text-xs font-medium px-3 h-8 rounded-md border ${
            viewMode === "liste"
              ? "bg-[#001489]/10 border-[#001489] text-[#001489] dark-text-active"
              : "bg-gray-50 dark:bg-muted border-transparent text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setViewMode("liste")}
        >
          Vue liste
        </button>
        <button
          type="button"
          className={`text-xs font-medium px-3 h-8 rounded-md border ${
            viewMode === "calendrier"
              ? "bg-[#001489]/10 border-[#001489] text-[#001489] dark-text-active"
              : "bg-gray-50 dark:bg-muted border-transparent text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setViewMode("calendrier")}
        >
          Vue calendrier
        </button>
        <ParticipantsFilterMenu value={participantsFilter} onChange={changeParticipantsFilter} pairReady={pairPoids != null} />
      </div>

      {error && <div className="text-xs text-red-600">{error}</div>}

      {viewMode === "liste" && result && (
        <>
          <div className="text-xs text-muted-foreground">
            {result.total} tournoi{result.total !== 1 ? "s" : ""} — page {result.page}/{result.totalPages}
          </div>
          <div className="space-y-3">
            {result.tournois.map((t: any, i: number) => (
              <TournoiCard
                key={t.idHomologation ?? `idx-${i}`}
                t={t}
                joueur1={joueur1}
                joueur2={joueur2}
                open={openTournoi === (t.idHomologation ?? `idx-${i}`)}
                onOpenChange={(next) => setOpenTournoi(next ? t.idHomologation ?? `idx-${i}` : null)}
              />
            ))}
          </div>
          {result.totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-2">
              <button className="text-xs px-3 h-8 rounded-md bg-gray-50 dark:bg-muted disabled:opacity-40" disabled={page <= 1} onClick={() => goPage(page - 1)}>
                ← Précédent
              </button>
              <span className="text-xs text-muted-foreground">
                Page {page} / {result.totalPages}
              </span>
              <button
                className="text-xs px-3 h-8 rounded-md bg-gray-50 dark:bg-muted disabled:opacity-40"
                disabled={page >= result.totalPages}
                onClick={() => goPage(page + 1)}
              >
                Suivant →
              </button>
            </div>
          )}
        </>
      )}

      {viewMode === "calendrier" && <TournoisCalendrier appliedFilters={appliedFilters} joueur1={joueur1} joueur2={joueur2} />}
    </div>
  );
}
